// The client realm's mirror scene (docs/ARCHITECTURE.md §7, §8; docs/spikes/esengine-capability.md (a), (d)). A
// per-war ESEngine Core + Scene holding the net components the room's ViewEncoder sends for this player, applied in
// order with decodeSpawn / decodeSnapshot / processDespawn through one entityMap (wire entity.id → mirror entity).
// Wire ids are the room realm's entity.id and are never gameplay identity: the mirror indexes entities by SimId, and
// checks the encoder's guarantees (a spawn never reuses a held id, a delta or despawn only names held ids) instead of
// letting ESEngine fall back to findEntityById, which would alias the client realm's own ids.

import {
  BinaryReader,
  Core,
  Scene,
  SyncOperation,
  decodeDespawn,
  decodeSnapshot,
  decodeSpawn,
  processDespawn,
  type Component,
  type ComponentType,
  type DecodeEntityResult,
  type Entity,
} from '@esengine/ecs-framework'
import { ALL_COMPONENTS, Building, GeoPosition, InvariantViolation, SimIdentity, StrikeFlight, type SimId, type ViewFrame } from '@taxwar/net'
import { PoseTrack, recordPose, type PresentationSystem } from './presentation'

const MIRROR_SCENE_NAME = 'taxwar-mirror'
/** Client-only components, registered with the net components before any entity exists. */
const CLIENT_COMPONENTS: readonly ComponentType<Component>[] = [PoseTrack]

export function getRequired<T extends Component>(entity: Entity, type: ComponentType<T>): T {
  const component = entity.getComponent(type)
  if (component === null) throw new InvariantViolation(`mirror entity '${entity.name}' is missing component ${type.name}`)
  return component
}

function removeFrom<T>(list: T[], item: T): void {
  const index = list.indexOf(item)
  if (index >= 0) list.splice(index, 1)
}

/** Inserts into an ascending list, keeping it sorted. */
function insertSorted(list: SimId[], simId: SimId): void {
  let i = list.length
  while (i > 0 && (list[i - 1] as SimId) > simId) i--
  list.splice(i, 0, simId)
}

/** Reads the wire entity id of a SPAWN op (uint8 operation, then uint32 LE id). */
function spawnedWireId(op: Uint8Array): number {
  const reader = new BinaryReader(op)
  reader.readUint8()
  return reader.readUint32()
}

export class MirrorWorld {
  /** Wire entity.id → mirror entity; ESEngine's decoders keep it current. */
  private readonly entityMap = new Map<number, Entity>()
  private readonly bySimId = new Map<number, Entity>()
  /** Entities with a PoseTrack, sampled after every frame. */
  private readonly posed: Entity[] = []
  private readonly buildingEntities: Entity[] = []
  /** SimIds of the strikes in flight in view, ascending. */
  private readonly strikeSimIds: SimId[] = []
  private readonly systems: PresentationSystem[] = []

  private readonly opHandlers: ReadonlyMap<number, (op: Uint8Array) => void> = new Map<number, (op: Uint8Array) => void>([
    [SyncOperation.SPAWN, (op: Uint8Array) => this.applySpawn(op)],
    [SyncOperation.DELTA, (op: Uint8Array) => this.applyDelta(op)],
    [SyncOperation.DESPAWN, (op: Uint8Array) => this.applyDespawn(op)],
  ])

  private constructor(private readonly scene: Scene) {}

  /** Creates the realm's Core with the mirror scene. One Match (one mirror) per realm at a time. */
  static create(): MirrorWorld {
    if (Core.Instance !== null) throw new InvariantViolation('an ESEngine Core already exists in this realm (one Match at a time)')
    Core.create({ debug: false })
    try {
      const scene = new Scene({ name: MIRROR_SCENE_NAME })
      for (const component of ALL_COMPONENTS) scene.componentRegistry.register(component)
      for (const component of CLIENT_COMPONENTS) scene.componentRegistry.register(component)
      Core.setScene(scene)
      return new MirrorWorld(scene)
    } catch (error) {
      Core.destroy()
      throw error
    }
  }

  /** Mirror entity currently holding this SimId (undefined while it is out of view). */
  find(simId: SimId): Entity | undefined {
    return this.bySimId.get(simId)
  }

  /** Every building entity in view (live array; do not hold across frames). */
  get buildings(): readonly Entity[] {
    return this.buildingEntities
  }

  /** SimIds of the strikes in flight in view, ascending (live array; do not hold across frames). */
  get strikes(): readonly SimId[] {
    return this.strikeSimIds
  }

  /** Applies one view frame's ops in order, then samples every positioned entity at the frame's tick. */
  applyFrame(frame: ViewFrame): void {
    const ops = frame.ops
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i] as Uint8Array
      const handler = this.opHandlers.get(op[0] as number)
      if (handler === undefined) throw new InvariantViolation(`view op with unexpected sync operation ${op[0]}`)
      handler(op)
    }
    for (let i = 0; i < this.posed.length; i++) {
      const entity = this.posed[i] as Entity
      const position = entity.getComponent(GeoPosition) as GeoPosition
      recordPose(entity.getComponent(PoseTrack) as PoseTrack, frame.tick, position.lat, position.lon, position.heading)
    }
  }

  /** Runs the presentation systems for one animation frame, then rethrows any error ESEngine swallowed. */
  update(dtSeconds: number): void {
    if (Core.scene !== this.scene) throw new InvariantViolation('the mirror scene is not the current ESEngine scene of this realm')
    Core.update(dtSeconds)
    for (let i = 0; i < this.systems.length; i++) {
      const fault = (this.systems[i] as PresentationSystem).latchedFault
      if (fault !== null) throw fault.error
    }
  }

  addSystem(system: PresentationSystem): void {
    this.scene.addSystem(system)
    this.systems.push(system)
  }

  removeSystem(system: PresentationSystem): void {
    const index = this.systems.indexOf(system)
    if (index < 0) throw new InvariantViolation('presentation system removed twice')
    this.systems.splice(index, 1)
    this.scene.removeSystem(system)
  }

  /**
   * Forgets everything held from the room, keeping the scene and its systems: an online rejoin's resync, whose next
   * frame spawns everything in view again (net/room-connection.ts).
   */
  reset(): void {
    this.scene.destroyEntities([...this.entityMap.values()])
    this.entityMap.clear()
    this.bySimId.clear()
    this.posed.length = 0
    this.buildingEntities.length = 0
    this.strikeSimIds.length = 0
  }

  /** Tears down Core with the mirror scene, its entities and systems, releasing the realm. */
  dispose(): void {
    this.systems.length = 0
    this.posed.length = 0
    this.buildingEntities.length = 0
    this.strikeSimIds.length = 0
    this.bySimId.clear()
    this.entityMap.clear()
    Core.destroy()
  }

  private applySpawn(op: Uint8Array): void {
    const wireId = spawnedWireId(op)
    if (this.entityMap.has(wireId)) throw new InvariantViolation(`spawn of wire entity ${wireId}, which is already held`)
    const decoded = decodeSpawn(this.scene, op, this.entityMap)
    if (decoded === null) throw new InvariantViolation('spawn op did not decode as a spawn')
    const entity = decoded.entity
    const simId = getRequired(entity, SimIdentity).simId as SimId
    if (this.bySimId.has(simId)) throw new InvariantViolation(`spawn of SimId ${simId}, which is already held`)
    this.bySimId.set(simId, entity)
    if (entity.hasComponent(GeoPosition)) {
      entity.addComponent(new PoseTrack())
      this.posed.push(entity)
    }
    if (entity.hasComponent(Building)) this.buildingEntities.push(entity)
    if (entity.hasComponent(StrikeFlight)) insertSorted(this.strikeSimIds, simId)
  }

  private applyDelta(op: Uint8Array): void {
    const decoded = decodeSnapshot(this.scene, op, this.entityMap)
    const entities = decoded.entities
    for (let i = 0; i < entities.length; i++) {
      const result = entities[i] as DecodeEntityResult
      // isNew: ESEngine created an entity for an unknown id; not in the map: it matched a client-realm id instead.
      if (result.isNew || !this.entityMap.has(result.entityId)) throw new InvariantViolation(`delta for wire entity ${result.entityId}, which is not held`)
    }
  }

  private applyDespawn(op: Uint8Array): void {
    const decoded = decodeDespawn(op)
    if (decoded === null) throw new InvariantViolation('despawn op did not decode as a despawn')
    for (const wireId of decoded.entityIds) {
      const entity = this.entityMap.get(wireId)
      if (entity === undefined) throw new InvariantViolation(`despawn of wire entity ${wireId}, which is not held`)
      const simId = getRequired(entity, SimIdentity).simId as SimId
      this.bySimId.delete(simId)
      removeFrom(this.posed, entity)
      removeFrom(this.buildingEntities, entity)
      removeFrom(this.strikeSimIds, simId)
    }
    processDespawn(this.scene, op, this.entityMap)
  }
}
